import { useEffect } from 'react';
import {
    addEventToGlobalContext,
    addTicketToGlobalContext,
    addUserGlobalEntityStartTrack,
    DEV_PREFIX,
    BizzaboSnowplowTracker,
    SchemasHelper,
    sendCustomClick,
} from '@bizzabo/widget-analytics';
import queryString from 'query-string';
import { useLocation } from 'react-router-dom';
import namespace from '../config/namespace';
import { Event } from '../event';
import { TicketType } from '../tickets';
import type { UserData } from '../people';
import useOnResizeAnalytic from './hooks/useOnResizeAnalytic';

const SNOWPLOW_APP_ID_PROD = 'web-ring'; // todo: to extract config to ring level

export const SNOWPLOW_APP_ID = namespace
    ? `${SNOWPLOW_APP_ID_PROD}${DEV_PREFIX}`
    : SNOWPLOW_APP_ID_PROD;

const env = namespace ? 'dev' : 'prod';

export const isSnowPlowTrackerEnabled = true;
export const snowplowInDevLikeInProd = false;

const getCustomPageUrlForEmbedMode = () => {
    const search = queryString.parse(location.search);
    const searchWithRemovedStyles = queryString.stringify({ ...search, userCss: '' });
    return `${location.origin}${location.pathname}?${searchWithRemovedStyles}`;
};

type UseAnalytics = ({
    event,
    user,
    tickets,
    isEmbedMode,
}: {
    event: Event;
    user: UserData;
    tickets: TicketType[];
    isEmbedMode: boolean;
}) => void;

const useAnalytics: UseAnalytics = ({ event, user, tickets, isEmbedMode }) => {
    useEffect(() => {
        const customPageURL = isEmbedMode ? getCustomPageUrlForEmbedMode() : undefined;
        BizzaboSnowplowTracker.dedicatedInitTracker({
            env,
            snowplowInDevLikeInProd,
            appId: SNOWPLOW_APP_ID,
            platform: 'web',
            bufferSize: 1,
            overrideConfig: {},
            customPageURL,
        });
    }, []);

    useEffect(() => {
        if (!event?.id) {
            return;
        }

        // TODO: Move all parsing logic per domain inside this project
        addEventToGlobalContext(
            {
                ...event,
                'account-id': event.accountId.toString(),
                'local-start-date': event.localStartDate,
                'local-end-date': event.localEndDate,
                'time-zone-id': event.timeZone,
            },
            isSnowPlowTrackerEnabled
        );
    }, [event]);

    useEffect(() => {
        if (!user?.id) {
            return;
        }
        const userEntity = [SchemasHelper.getUserEntity(user)];
        BizzaboSnowplowTracker.removeGlobalContext(userEntity);
        addUserGlobalEntityStartTrack(user, isSnowPlowTrackerEnabled);
    }, [user]);

    useEffect(() => {
        if (!tickets?.length) {
            return;
        }
        const ticket = tickets.find(({ virtualCheckin }) => !!virtualCheckin) || tickets[0];
        addTicketToGlobalContext(ticket, isSnowPlowTrackerEnabled);
    }, [tickets]);

    useOnResizeAnalytic();
};

export const trackSystemEntityEvent = (
    systemName: string,
    actionDescription: string,
    description?: string,
    action?: string
) => {
    if (isSnowPlowTrackerEnabled && systemName) {
        const systemEntity = SchemasHelper.getSystemInfoEventEntity({
            systemName,
            actionDescription,
            description,
            action,
        });
        BizzaboSnowplowTracker.trackSelfDescribingEvent(systemEntity);
    }
};

export const useTrackUserGetToRingFrom = () => {
    const { search } = useLocation();
    const { source } = queryString.parse(search);
    useEffect(() => {
        if (isSnowPlowTrackerEnabled && source) {
            const systemEntity = SchemasHelper.getSystemInfoEventEntity({
                systemName: 'RING_ONBOARDING_LOADED',
                actionDescription: source,
            });
            BizzaboSnowplowTracker.sendSystemWithPageViewCheck(systemEntity);
        }
    }, [source]);
};

export const sendPageView = (description?: string) => {
    if (isSnowPlowTrackerEnabled) {
        const pageView = SchemasHelper.getPageViewEventEntity({ description });

        BizzaboSnowplowTracker.trackSelfDescribingEvent(pageView);
    }
};

export const trackCustomClickEvent = clickData => {
    sendCustomClick(clickData, isSnowPlowTrackerEnabled);
};

export const trackPingEvent = (data: {
    category: string;
    description?: string;
    action?: string;
}) => {
    BizzaboSnowplowTracker.trackSelfDescribingEvent(SchemasHelper.getPingEventEntity(data));
};

export default useAnalytics;
